"use client";

// Visual for the "Send payments easily" bento card (design handoff:
// pay-across-the-world-animation.md): a bank account bar and an upcoming
// payment tile; once "Pay" is pressed it cross-fades to a dot globe with the
// payment arcing from New York to Dublin. A fixed 560x230 stage on a 10s rAF
// loop, scaled down to fit narrower cards.

import { Check } from "iconoir-react";
import * as React from "react";

import { cn } from "@/lib/utils";

import {
  drawGlobeDots,
  DUBLIN,
  getGlobeDots,
  getRoute,
  GLOBE_HEIGHT,
  GLOBE_WIDTH,
  NEW_YORK,
  projectLatLon,
} from "./globe-geometry";
import { getPayFrame, PAY_AMOUNT, PAY_REST_SECONDS } from "./pay-timeline";
import { useLoopSeconds, useStageScale } from "./use-stage-loop";

interface PayWorldVisualProps {
  bankAccountLabel: string;
  availableBalanceLabel: string;
  upcomingPaymentLabel: string;
  dueTodayLabel: string;
  wireLabel: string;
  payLabel: string;
  sentLabel: string;
  className?: string;
}

const TILE_SHADOW = "shadow-[0_2px_20px_rgba(22,21,23,0.08)]";
const CHIP =
  "border-grey-100 bg-grey-0 absolute flex items-center gap-2.5 rounded-[10px] border py-2 pr-3 pl-2 text-[13px] whitespace-nowrap transition-opacity duration-300 " +
  TILE_SHADOW;
const CHIP_ICON =
  "flex size-[26px] items-center justify-center rounded-md text-[11px] font-semibold text-white";

const cents = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const clamp = (x: number, min: number, max: number) =>
  Math.min(max, Math.max(min, x));

export function PayWorldVisual({
  bankAccountLabel,
  availableBalanceLabel,
  upcomingPaymentLabel,
  dueTodayLabel,
  wireLabel,
  payLabel,
  sentLabel,
  className,
}: PayWorldVisualProps) {
  const root = React.useRef<HTMLDivElement>(null);
  const canvas = React.useRef<HTMLCanvasElement>(null);
  const seconds = useLoopSeconds(root, PAY_REST_SECONDS);
  const scale = useStageScale(root, GLOBE_WIDTH);
  // Below 536px the card shows a full-size, cropped globe (see the CSS classes).
  const [narrow, setNarrow] = React.useState(false);
  React.useEffect(() => {
    const mq = window.matchMedia("(max-width: 535px)");
    const sync = () => setNarrow(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);
  const boxWidth = scale * GLOBE_WIDTH;

  const f = getPayFrame(seconds);
  const route = getRoute(f.rotation);
  const source = projectLatLon(NEW_YORK.lat, NEW_YORK.lon, f.rotation);
  const destination = projectLatLon(DUBLIN.lat, DUBLIN.lon, f.rotation);

  // Dots are painted straight to the canvas each frame, only while the globe
  // is showing (the dot field is built once, lazily).
  React.useEffect(() => {
    const el = canvas.current;
    if (!el) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    if (el.width !== GLOBE_WIDTH * dpr) {
      el.width = GLOBE_WIDTH * dpr;
      el.height = GLOBE_HEIGHT * dpr;
    }
    const ctx = el.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (!f.globeVisible) {
      ctx.clearRect(0, 0, GLOBE_WIDTH, GLOBE_HEIGHT);
      return;
    }
    drawGlobeDots(ctx, getGlobeDots(), f.rotation);
  }, [f.globeVisible, f.rotation]);

  const routePath = route
    .map((p, i) => `${i ? "L" : "M"}${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(" ");
  const steps = route.length - 1;
  const index = Math.min(steps, Math.floor(f.arc * steps));
  const fraction = f.arc * steps - index;
  const from = route[index]!;
  const to = route[Math.min(steps, index + 1)]!;
  const packet = {
    x: from.x + (to.x - from.x) * fraction,
    y: from.y + (to.y - from.y) * fraction,
  };
  const sourceRing = f.sourcePulse > 0 && f.sourcePulse < 1;
  const destinationRing = f.destinationPulse > 0 && f.destinationPulse < 1;

  return (
    <div
      ref={root}
      aria-hidden
      className={cn(
        "relative mt-auto min-[536px]:absolute min-[536px]:inset-x-6 min-[536px]:mt-0 min-[536px]:h-[calc(var(--s)*230px)] md:inset-x-10",
        className
      )}
      style={{ "--s": scale } as React.CSSProperties}
    >
      <div className="relative mx-auto w-full min-[536px]:h-[230px] min-[536px]:w-[560px] min-[536px]:origin-top-left min-[536px]:scale-[var(--s)] min-[536px]:overflow-hidden min-[536px]:rounded-[16px]">
        {/* UI layer */}
        <div
          className="relative flex flex-col gap-3.5 transition-[opacity,transform] duration-[400ms] min-[536px]:absolute min-[536px]:inset-0"
          style={{
            opacity: f.globeVisible ? 0 : 1,
            transform: `translateY(${f.globeVisible ? -8 : 0}px)`,
          }}
        >
          <div
            className={cn(
              "box-border flex h-24 items-center justify-between rounded-[16px] px-[22px] py-[18px] text-white",
              TILE_SHADOW
            )}
            style={{
              background:
                "linear-gradient(90deg, #001eff 0%, #0a14b8 60%, #000633 100%)",
            }}
          >
            <div className="flex flex-col gap-1.5">
              <span className="flex items-center gap-2 text-[13px] font-medium whitespace-nowrap">
                <span className="flex size-[22px] items-center justify-center rounded-full bg-[#e0e6ff] text-[11px] font-semibold text-[#001eff]">
                  W
                </span>
                {bankAccountLabel}
                <span className="ml-1 hidden text-[11px] font-normal text-[#b9c3ff] tabular-nums min-[420px]:inline">
                  ·· 8323
                </span>
              </span>
              <div className="text-[26px] leading-[1.1] font-medium tracking-[-0.03em] whitespace-nowrap tabular-nums">
                <span className="text-[#b9c3ff]">$</span>
                {cents.format(f.balance)}
              </div>
            </div>
            <div className="text-right text-[11px] leading-[1.5] whitespace-nowrap text-[#b9c3ff]">
              {availableBalanceLabel}
              <br />
              <span className="font-medium text-white">USD</span>
            </div>
          </div>

          <div
            className={cn(
              "border-grey-100 bg-grey-0 box-border flex flex-col justify-between gap-3 rounded-[16px] border px-[22px] py-[18px] min-[536px]:h-[120px] min-[536px]:gap-0",
              TILE_SHADOW
            )}
          >
            <div className="flex items-center justify-between">
              <span className="text-grey-950 text-[13px] font-medium">
                {upcomingPaymentLabel}
              </span>
              <span className="text-pulse-700 rounded-[4px] bg-[#e0e6ff] px-2 py-0.5 text-[11px] font-medium">
                {dueTodayLabel}
              </span>
            </div>
            <div className="flex flex-col gap-3 min-[536px]:flex-row min-[536px]:items-center min-[536px]:justify-between min-[536px]:gap-4">
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-[#0d0e12] text-[12px] font-semibold text-white">
                  TI
                </span>
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-grey-950 text-[14px] font-medium whitespace-nowrap">
                    Textile Inc.
                  </span>
                  <span className="text-grey-500 text-[12px] whitespace-nowrap">
                    {wireLabel}
                  </span>
                </span>
              </div>
              <div className="flex shrink-0 items-center justify-between gap-3.5 min-[536px]:justify-start">
                <span className="text-grey-950 text-[18px] font-medium tracking-[-0.02em] tabular-nums">
                  ${cents.format(PAY_AMOUNT)}
                </span>
                <div className="relative">
                  <div
                    className={cn(
                      "text-grey-0 rounded-full px-[18px] py-[7px] text-[13px] leading-[1.4] font-medium transition-[background,transform] duration-150",
                      f.pressed ? "scale-[0.93] bg-[#0016c4]" : "bg-pulse-700"
                    )}
                  >
                    {payLabel}
                  </div>
                  <svg
                    width="22"
                    height="26"
                    viewBox="0 0 22 26"
                    className="pointer-events-none absolute top-1/2 left-[40%] drop-shadow-md"
                    style={{
                      opacity: f.cursor.opacity,
                      transform: `translate(${f.cursor.x}px, ${f.cursor.y}px)`,
                    }}
                  >
                    <path
                      d="M2 1.5v19l5.2-4.6 3.4 7.6 3.2-1.4-3.4-7.5H18z"
                      fill="#0c0d0d"
                      stroke="#ffffff"
                      strokeWidth="1.6"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Globe layer */}
        <div
          className={cn(
            "border-grey-100 bg-grey-0 pointer-events-none absolute inset-0 overflow-hidden rounded-[16px] border transition-opacity duration-500",
            TILE_SHADOW
          )}
          style={{ opacity: f.globeVisible ? 1 : 0 }}
        >
          {/* Narrow: full-size globe, cropped to the route (x 139-400) around its centre. */}
          <div className="absolute bottom-0 left-1/2 -ml-[270px] h-[230px] w-[560px] min-[536px]:top-0 min-[536px]:bottom-auto min-[536px]:left-0 min-[536px]:ml-0">
            <canvas
              ref={canvas}
              width={GLOBE_WIDTH}
              height={GLOBE_HEIGHT}
              className="absolute inset-0"
              style={{ width: GLOBE_WIDTH, height: GLOBE_HEIGHT }}
            />
            <svg
              viewBox={`0 0 ${GLOBE_WIDTH} ${GLOBE_HEIGHT}`}
              width={GLOBE_WIDTH}
              height={GLOBE_HEIGHT}
              className="absolute inset-0"
            >
              <defs>
                <linearGradient id="wc-pay-route" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0" stopColor="#001eff" />
                  <stop offset="0.55" stopColor="#8a28f3" />
                  <stop offset="1" stopColor="#fe634d" />
                </linearGradient>
              </defs>
              <path
                d={routePath}
                fill="none"
                stroke="url(#wc-pay-route)"
                strokeWidth="1.5"
                strokeLinecap="round"
                pathLength={1}
                strokeDasharray={`${f.arc} 1`}
                strokeDashoffset={0}
              />
              <circle
                cx={source.x}
                cy={source.y}
                r="3.5"
                fill="#ffffff"
                stroke="#001eff"
                strokeWidth="1.5"
              />
              <circle
                cx={source.x}
                cy={source.y}
                r={5 + f.sourcePulse * 18}
                fill="none"
                stroke="#001eff"
                strokeWidth="1"
                opacity={sourceRing ? 1 - f.sourcePulse : 0}
              />
              <circle
                cx={destination.x}
                cy={destination.y}
                r="3.5"
                fill="#ffffff"
                stroke="#fe634d"
                strokeWidth="1.5"
                opacity={f.destinationLit ? 1 : 0.35}
              />
              <circle
                cx={destination.x}
                cy={destination.y}
                r={5 + f.destinationPulse * 18}
                fill="none"
                stroke="#fe634d"
                strokeWidth="1"
                opacity={destinationRing ? 1 - f.destinationPulse : 0}
              />
              <circle
                cx={packet.x}
                cy={packet.y}
                r="3"
                fill="#ffffff"
                stroke="#8a28f3"
                strokeWidth="1.5"
                opacity={f.packetVisible ? 1 : 0}
              />
            </svg>

            <div
              className={CHIP}
              style={{
                left: narrow
                  ? clamp(source.x - 20, 278 - boxWidth / 2, 240)
                  : clamp(source.x - 120, 8, 240),
                top: Math.min(180, source.y + 12),
                opacity: f.sourceChipVisible ? 1 : 0,
              }}
            >
              <span className={cn(CHIP_ICON, "bg-pulse-700")}>W</span>
              <span className="font-medium">$24,500</span>
              <span className="text-grey-500">USD</span>
            </div>
            <div
              className={CHIP}
              style={{
                ...(narrow
                  ? { right: 298 - boxWidth / 2 }
                  : { left: clamp(destination.x + 10, 260, 405) }),
                top: Math.min(180, destination.y + 12),
                opacity: f.destinationChipVisible ? 1 : 0,
              }}
            >
              <span className={cn(CHIP_ICON, "bg-[#0d0e12]")}>TI</span>
              <span className="font-medium">€22,540</span>
              <span className="text-grey-500">EUR</span>
            </div>
          </div>
          {/* Outside the (cropped) globe stage so it stays inside the card on narrow screens. */}
          <div
            className={cn(
              "border-grey-100 bg-grey-0 text-grey-950 absolute top-3.5 left-4 inline-flex max-w-[calc(100%-2rem)] items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] transition-opacity duration-300",
              TILE_SHADOW
            )}
            style={{ opacity: f.confirmationVisible ? 1 : 0 }}
          >
            <Check aria-hidden className="size-3.5 text-[#3fa95b]" />
            {sentLabel}
          </div>
        </div>
      </div>
    </div>
  );
}
